import { isForgeCustomField } from '@atlassian/jira-forge-field-base/src/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	FORGE_NUMBER_GQL_FIELD,
	FORGE_STRING_GQL_FIELD,
} from '@atlassian/jira-platform-field-config/src/index.tsx';

import {
	FORGE_CUSTOM_FIELD_TYPE_NUMBER,
	FORGE_CUSTOM_FIELD_TYPE_STRING,
	type ForgeCustomFieldTypes,
} from '../../constants.tsx';

function getFieldTypeFromGQLType(gqlType: string): ForgeCustomFieldTypes | undefined {
	const map: { [key: string]: ForgeCustomFieldTypes } = {
		[FORGE_STRING_GQL_FIELD]: FORGE_CUSTOM_FIELD_TYPE_STRING,
		[FORGE_NUMBER_GQL_FIELD]: FORGE_CUSTOM_FIELD_TYPE_NUMBER,
	};

	return map[gqlType];
}

/**
 * Normalizes the internal gqlType to the public fieldType.
 *
 * This function determines the correct field type to use based on whether the field is a Forge
 * custom field and whether the feature gate for Forge UIM support is enabled. It provides
 * backward compatibility by falling back to the original field type when normalization isn't
 * applicable or available.
 *
 * @param fieldType - The original field type identifier (may be undefined)
 * @param gqlType - The GraphQL field type identifier (may be undefined)
 * @returns The normalized field type if applicable, otherwise returns the original fieldType
 *
 * @example
 * ```typescript
 * // With feature gate enabled and Forge custom field
 * const normalized = getNormalizedType('ari:cloud:ecosystem::extension/00000000-1111-2222-3333-444444444444/custom-field-type', FORGE_STRING_GQL_FIELD);
 * // Returns: FORGE_CUSTOM_FIELD_TYPE_STRING
 *
 * // With feature gate disabled
 * const original = getNormalizedType('ari:cloud:ecosystem::extension/00000000-1111-2222-3333-444444444444/custom-field-type', FORGE_STRING_GQL_FIELD);
 * // Returns: 'ari:cloud:ecosystem::extension/00000000-1111-2222-3333-444444444444/custom-field-type'
 *
 * // With non-Forge field
 * const nonForge = getNormalizedType('standard_field', 'standard_gql_type');
 * // Returns: 'standard_field'
 * ```
 */
export const getNormalizedType = (fieldType: string | undefined, gqlType: string | undefined) => {
	if (!fg('ditto_forge_uim_support_for_fcf')) {
		return fieldType;
	}
	const isFCF = isForgeCustomField(fieldType);
	if (!isFCF || !gqlType) {
		return fieldType;
	}
	const normalizedType = getFieldTypeFromGQLType(gqlType);
	return normalizedType || fieldType;
};
